import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue";
const _hoisted_1 = ["id"];
const _hoisted_2 = { "data-testid": "collecte-form-archive-modal" };
const _hoisted_3 = {
    class: "text-sm text-(--colors-text-body-subtle) mb-4",
    "data-testid": "collecte-form-archive-warning"
};
const _hoisted_4 = {
    key: 0,
    class: "mt-2"
};
const _hoisted_5 = {
    key: 1,
    class: "mt-4 text-sm text-(--colors-text-error)",
    role: "alert",
    "data-testid": "collecte-form-archive-error"
};
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Button, Modal, SelectInput } from "@eudonet/opal";
import { useDonationFormStore, LIST_FORMS_MAX_PER_PAGE, } from "../../stores/useDonationFormStore";
import { resolveApiErrorMessage } from "@/infrastructure/services/ApiErrorService";
import { useAppToast } from "@/shared/composables/useAppToast";
import { logger } from "@/infrastructure/services/LoggerService";
export default /*@__PURE__*/ _defineComponent({
    __name: 'FormArchiveModal',
    props: /*@__PURE__*/ _mergeModels({
        sourceId: {},
        sourceTitle: {},
        archive: { type: Function }
    }, {
        "modelValue": { type: Boolean, ...{ required: true } },
        "modelModifiers": {},
    }),
    emits: ["update:modelValue"],
    setup(__props) {
        const ARCHIVE_ERROR_KEYS = {
            "Form.InvalidTransition": "collecte.forms.archive.errors.invalidTransition",
            "Form.RedirectToFormId.Invalid": "collecte.forms.archive.errors.redirectInvalid",
            "Form.RedirectToFormId.SelfReference": "collecte.forms.archive.errors.redirectSelf",
            "Form.RedirectToFormId.NotAvailable": "collecte.forms.archive.errors.redirectUnavailable",
            "Form.NotFound": "collecte.forms.archive.errors.notFound",
        };
        const props = __props;
        const open = _useModel(__props, "modelValue");
        const titleId = useId();
        const { t } = useI18n();
        const store = useDonationFormStore();
        const { toastSuccess, toastError } = useAppToast();
        const redirectToFormId = ref("");
        const targets = ref([]);
        const isLoadingTargets = ref(false);
        const targetsLoadFailed = ref(false);
        const targetsTruncated = ref(false);
        const submitError = ref(null);
        const isArchiving = ref(false);
        let sessionSeq = 0;
        const options = computed(() => {
            const excluded = props.sourceId.toLowerCase();
            const candidates = targets.value.filter((form) => form.id.toLowerCase() !== excluded);
            if (candidates.length === 0) {
                return [];
            }
            return [
                { value: "", label: t("collecte.forms.archive.redirectNone") },
                ...candidates.map((form) => ({ value: form.id, label: form.title || form.publicSlug })),
            ];
        });
        const hasTargets = computed(() => options.value.length > 0);
        const isPickerDisabled = computed(() => isLoadingTargets.value || !hasTargets.value);
        const isSubmitDisabled = computed(() => isLoadingTargets.value || targetsLoadFailed.value || isArchiving.value);
        const pickerError = computed(() => targetsLoadFailed.value ? t("collecte.forms.archive.redirectLoadError") : undefined);
        const pickerHint = computed(() => {
            if (targetsLoadFailed.value) {
                return "";
            }
            if (targetsTruncated.value) {
                return t("collecte.forms.archive.redirectTruncated", { count: LIST_FORMS_MAX_PER_PAGE });
            }
            if (isLoadingTargets.value || hasTargets.value) {
                return t("collecte.forms.archive.redirectHint");
            }
            return t("collecte.forms.archive.redirectEmpty");
        });
        async function loadTargets() {
            const seq = ++sessionSeq;
            isLoadingTargets.value = true;
            targetsLoadFailed.value = false;
            try {
                const pool = await store.fetchPublishedForms();
                if (seq !== sessionSeq)
                    return;
                targets.value = pool.forms;
                targetsTruncated.value = pool.truncated;
            }
            catch (err) {
                if (seq !== sessionSeq)
                    return;
                targets.value = [];
                targetsTruncated.value = false;
                targetsLoadFailed.value = true;
                logger.error("[FormArchiveModal] Failed to load redirect targets", err);
            }
            finally {
                if (seq === sessionSeq)
                    isLoadingTargets.value = false;
            }
        }
        // Reset EVERY time the dialog opens and re-collect the candidates, because the component stays
        // mounted across rows: a leftover selection would otherwise ride under a title naming a different
        // form, and the pool is state-dependent (a form published or archived since the last open).
        watch(open, (isOpen) => {
            if (!isOpen)
                return;
            redirectToFormId.value = "";
            submitError.value = null;
            targets.value = [];
            targetsTruncated.value = false;
            void loadTargets();
        });
        async function submit() {
            const seq = sessionSeq;
            submitError.value = null;
            isArchiving.value = true;
            try {
                const runArchive = props.archive ?? store.archiveForm;
                await runArchive(props.sourceId, redirectToFormId.value || null);
                if (seq !== sessionSeq)
                    return;
                open.value = false;
                toastSuccess("collecte.forms.archive.success");
            }
            catch (e) {
                if (seq !== sessionSeq)
                    return;
                const message = resolveApiErrorMessage(e, ARCHIVE_ERROR_KEYS, "collecte.forms.archive.errors.saveFailed");
                if (open.value) {
                    submitError.value = message;
                }
                else {
                    toastError("collecte.forms.archive.errors.saveFailed");
                }
            }
            finally {
                if (seq === sessionSeq)
                    isArchiving.value = false;
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: open.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((open).value = $event)),
                size: "md",
                "aria-labelledby": _unref(titleId),
                dismissable: !isArchiving.value,
                "close-on-escape": !isArchiving.value,
                "show-close-button": !isArchiving.value
            }, {
                header: _withCtx(() => [
                    _createElementVNode("h2", {
                        id: _unref(titleId),
                        class: "text-base font-semibold text-(--colors-text-heading)",
                        "data-testid": "collecte-form-archive-title"
                    }, _toDisplayString(_ctx.$t("collecte.forms.archive.title", { title: __props.sourceTitle })), 9 /* TEXT, PROPS */, _hoisted_1)
                ]),
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        variant: "secondary",
                        label: _ctx.$t('common.cancel'),
                        disabled: isArchiving.value,
                        "data-testid": "collecte-form-archive-cancel",
                        onClick: _cache[1] || (_cache[1] = ($event) => (open.value = false))
                    }, null, 8 /* PROPS */, ["label", "disabled"]),
                    _createVNode(_unref(Button), {
                        variant: "primary",
                        label: _ctx.$t('collecte.forms.archive.submit'),
                        loading: isArchiving.value,
                        disabled: isSubmitDisabled.value,
                        "data-testid": "collecte-form-archive-submit",
                        onClick: submit
                    }, null, 8 /* PROPS */, ["label", "loading", "disabled"])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("p", _hoisted_3, _toDisplayString(_ctx.$t("collecte.forms.archive.warning")), 1 /* TEXT */),
                        _createVNode(_unref(SelectInput), {
                            modelValue: redirectToFormId.value,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((redirectToFormId).value = $event)),
                            label: _ctx.$t('collecte.forms.archive.redirectLabel'),
                            placeholder: _ctx.$t('collecte.forms.archive.redirectPlaceholder'),
                            options: options.value,
                            disabled: isPickerDisabled.value,
                            hint: pickerHint.value,
                            error: pickerError.value,
                            "full-width": "",
                            "data-testid": "collecte-form-archive-redirect"
                        }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "options", "disabled", "hint", "error"]),
                        (targetsLoadFailed.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                _createVNode(_unref(Button), {
                                    variant: "secondary",
                                    size: "sm",
                                    label: _ctx.$t('collecte.forms.retry'),
                                    "data-testid": "collecte-form-archive-retry",
                                    onClick: loadTargets
                                }, null, 8 /* PROPS */, ["label"])
                            ]))
                            : _createCommentVNode("v-if", true),
                        (submitError.value)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(submitError.value), 1 /* TEXT */))
                            : _createCommentVNode("v-if", true)
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "aria-labelledby", "dismissable", "close-on-escape", "show-close-button"]));
        };
    }
});
